/**
 * "Who's Out Today" widget — renders the approved-leave roster for the
 * current day. Reuses the existing /leave/team-availability endpoint,
 * which is org-scoped (or manager-scoped for Managers).
 */
import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Sun } from "lucide-react";
import { getTeamAvailability, type WhosOutEntry } from "@/api/directory";

const TYPE_LABELS: Record<string, string> = {
  vacation: "Vacation",
  sick: "Sick",
  personal: "Personal",
  parental: "Parental",
  bereavement: "Bereavement",
  unpaid: "Unpaid",
};

export function WhosOutWidget() {
  const [out, setOut] = useState<WhosOutEntry[] | null>(null);

  useEffect(() => {
    getTeamAvailability()
      .then((r) => setOut(r.out))
      .catch(() => setOut([]));
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-warning" />
          Who's out today
        </CardTitle>
      </CardHeader>
      {out === null ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : out.length === 0 ? (
        <p className="text-sm text-text-muted">
          Everyone's in — no approved leave for today.
        </p>
      ) : (
        <div className="space-y-2">
          {out.slice(0, 8).map((e) => (
            <div
              key={e.employee_id}
              className="flex items-center gap-3 py-1.5"
            >
              <Avatar src={e.avatar_url} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  {e.name}
                </p>
                <p className="text-xs text-text-muted truncate">{e.title}</p>
              </div>
              <Badge variant="warning">
                {TYPE_LABELS[e.leave_type] || e.leave_type}
              </Badge>
            </div>
          ))}
          {out.length > 8 && (
            <p className="text-xs text-text-muted pt-1">
              + {out.length - 8} more
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
